import { useMutation } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { adaptAddVisitaPresencialAECambioSondaPHInput, SAVE_PRESENCIAL_AE_CAMBIO_SONDA, SAVE_PRESENCIAL_AE_CONTROL_PH } from "../../../../../../data/graphQL/mutations/Visitas/saveCambioSondaYControlPH";

import { useStepSave } from "../../../../hooks/useStepSave";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { validateStepFields } from "../../../../utils/visitaStepValidation";

const InputLabelAviso = ({ label, textColor }) => (
  <Box
    sx={{
      bgcolor: '#e7e7e7',
      padding: 1,
      borderRadius: 1,
      color: textColor
    }}
  >
    {label}
  </Box>
);

const InputLabelTipoCaracteristica = ({ label }) => (
  <label style={{ textDecoration: 'underline' }}>{label}</label>
)

export default function ConstanciaColocacionSonda({ esUrgencia, readOnly, onSaveRef, onExposeFormatted }) {
  const { deleteArchivo } = useDeleteArchivoVisita();

  const [formData, setFormData] = useState({});

  const { visita, setVisita, saveArchivoVisita } = useVisita();

  const [saveCambioSonda] = useMutation(SAVE_PRESENCIAL_AE_CAMBIO_SONDA);
  const [saveControlPH] = useMutation(SAVE_PRESENCIAL_AE_CONTROL_PH);

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.SONDA
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({
      idArchivo: file.idArchivo,
      name: name,
      handleChange
    });
  };


  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (input.onlyCambioSonda && visita.cambioSonda !== true) {
          newInput.hidden = true;
        }

        if (input.onlyControlPh && visita.controlPh !== true) {
          newInput.hidden = true;
        }

        if (esUrgencia) newInput.required = false;

        if (readOnly) newInput.disabled = true;

        return newInput;
      });
      return {
        ...group,
        inputs: formattedInputs
      };
    });
  }, [visita, readOnly, esUrgencia]);

  useStepSave(onSaveRef, async (validate) => {

    if (validate) validateStepFields({ formattedGroups, formData });

    const save = visita.cambioSonda ? saveCambioSonda : saveControlPH;

    const { data } = await save({ variables: { input: adaptAddVisitaPresencialAECambioSondaPHInput(formData) } });
    const idPresencialAE = data?.saveVisitaPresencialAECambioSonda?.idPresencialAE || data?.saveVisitaPresencialAEControlPH?.idPresencialAE;

    if (!idPresencialAE) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData
    }));
  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'procedimientoPresencialAE',
      stepName: 'Procedimiento',
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.map((group, gi) =>
        <CardBox key={gi} title={group.title}>
          <Grid container spacing={2}>
            {group.inputs.
              filter((inp) => !inp.hidden).
              map((input) =>
                <Grid size={{ xs: 12 }} key={input.name}>
                  {input.type !== 'labelAviso' && input.type !== 'tipoCaracteristica' && <RenderInputByTypeMobile
                    key={input.name}
                    label={input.displayName}
                    value={formData[input.name]}
                    handleChange={input.name === 'fotoSonda' ? handleChangeFile : handleChange}
                    handleDelete={input.name === 'fotoSonda' ? onDeleteImage : null}
                    input={input} />}
                  {(input.type === 'labelAviso') &&
                    <InputLabelAviso label={input.displayName} textColor={input.textColor} />
                  }
                  {(input.type === 'tipoCaracteristica') &&
                    <InputLabelTipoCaracteristica label={input.displayName} />
                  }
                </Grid>
              )}
          </Grid>
        </CardBox>
      )}
    </>);

}


const groups = [
  {
    title: null,
    inputs: [
      {
        name: "controlUbicacionPH",
        displayName:
          "¿Se realiza control de ubicación a través de medición de PH gástrico (método colorímetro)?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "tiempoAyunoPrevio",
        displayName: "Tiempo de ayuno previo a la extracción de la muestra (hs)",
        longLabel: true,
        type: "int",
        required: true
      },
      {
        name: "cantidadMuestraExtraida",
        displayName: "Cantidad de muestra extraída (ml)",
        type: "int",
        required: true
      },
      {
        name: "fotoSonda",
        displayName: "Foto de Sonda",
        type: "file",
        validExtensions: ["JPG", "JPEG", "PNG"],
        maxFileSize: 2,
        required: true,
        onlyCambioSonda: true
      },
      {
        name: "tipoSonda",
        displayName: "Tipo Sonda",
        type: "long_string",
        maxLength: 250,
        required: true,
        onlyCambioSonda: true
      },
      {
        name: "lote",
        displayName: "Lote",
        type: "long_string",
        maxLength: 250,
        required: true,
        onlyCambioSonda: true
      },
      {
        name: "vencimientoLote",
        displayName: "Vencimiento",
        type: "date",
        required: true,
        disablePast: true,
        onlyCambioSonda: true
      }]

  },
  {
    title: "Características macroscópicas del material extraído",
    inputs: [
      {
        name: "labelNoInfundir",
        displayName: "No infundir y consultar con Médico de seguimiento cuando:",
        type: "labelAviso",
        textColor: "#e30f0fff"
      },
      {
        name: "aspectoNoInfundir",
        displayName: "Aspecto:",
        type: "tipoCaracteristica",
      },
      {
        name: "aspectoSimilMocoLeche",
        displayName:
          'Mucoso, gelatinoso, espeso ("similmoco" o "esputo")',
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "aspectoSimilLecheCortada",
        displayName: 'Grumoso (símil "Leche cortada" o "ricota")',
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "colorNoInfundir",
        displayName: "Color:",
        type: "tipoCaracteristica",
      },
      {
        name: "colorLechoso",
        displayName: "Lechoso blanquecino",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "colorRojizo",
        displayName: "Rojizo",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "colorNegruzco",
        displayName: "Negruzco",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "labelAutorizarInfusion",
        displayName: "Autorizar infusión cuando:",
        type: "labelAviso",
        textColor: "#18912c"
      },
      {
        name: "aspectoAutorizar",
        displayName: "Aspecto:",
        type: "tipoCaracteristica",
      },
      {
        name: "autorizaLiquidoAcuoso",
        displayName: 'Líquido, acuoso ("simil agua")',
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "autorizaFilante",
        displayName:
          'Filante (símil "punto caramelo o almíbar espeso")',
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "colorAutorizar",
        displayName: "Color:",
        type: "tipoCaracteristica",
      },
      {
        name: "autorizaColorAmarronado",
        displayName: "Amarronado",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "autorizaColorAmarillento",
        displayName: "Amarillento",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "autorizaColorVerdeOscuro",
        displayName: "Verde Oscuro",
        type: "boolean",
        lightTitle: true,
        required: true
      }]

  }];